import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';
import { integerToCurrency } from '@actual-app/core/shared/util';

import { cartao, cor, espaco, texto } from '#components/finapp/estilo';
import type { LancamentoPendente } from '#components/finapp/motor/filaPendencias';
import { Checkbox } from '#components/forms';

type Props = {
  /** Já FATIADO pela tela — ver `restantes`. */
  itens: LancamentoPendente[];
  /**
   * Quantos lançamentos existem além dos exibidos. `0` esconde o "mostrar
   * mais". Existe porque a primeira sincronização do Pluggy trouxe 1.125
   * lançamentos: desenhar os 1.125 de uma vez trava a tela e, pior, faz a
   * fila parecer infinita logo no primeiro olhar.
   */
  restantes: number;
  onMostrarMais: () => void;
  /**
   * Ids em que o dono DESMARCOU o aprendizado. Conjunto do que está
   * desligado (e não do que está ligado) porque o padrão é LIGADO — ver o
   * JSDoc do checkbox abaixo.
   */
  aprendizadoDesligado: ReadonlySet<string>;
  onAlternarAprendizado: (id: string) => void;
  onClassificar: (item: LancamentoPendente) => void;
};

const valorDaLinha = {
  ...texto.corpo,
  fontVariantNumeric: 'tabular-nums' as const,
};

/** Ver o JSDoc de `acaoDeTexto` em `PrecisaAgora.tsx` — mesmo estilo, mesma razão. */
const acaoDeTexto = {
  ...texto.corpo,
  color: cor.acento,
  cursor: 'pointer',
  background: 'none',
  border: 'none',
  padding: 0,
};

const larguraData = 56;
const larguraValor = 110;

/**
 * Faixa **"Sem categoria"** (spec §5.1) — a fila de verdade: o que o banco
 * trouxe e ninguém classificou.
 *
 * ⭐ O GESTO (spec §5.2): "Classificar" abre o modal nativo
 * `'category-autocomplete'` — o mesmo que outras 7 telas do app já usam.
 * Um dispatch, zero componente novo. Quem dispara é a tela
 * (`Pendencias.tsx`); aqui só existe o callback.
 *
 * ⛔ Sem divisória entre linhas e sem borda em volta: contraste de fundo e
 * altura de linha fazem a separação (direção "Extrato", regras 1 e 3).
 */
export function SemCategoria({
  itens,
  restantes,
  onMostrarMais,
  aprendizadoDesligado,
  onAlternarAprendizado,
  onClassificar,
}: Props) {
  const { t } = useTranslation();

  if (itens.length === 0) return null;

  return (
    <View style={{ ...cartao, gap: 10 }} data-testid="sem-categoria">
      <Text style={texto.tituloBloco}>
        <Trans>Sem categoria</Trans>
      </Text>

      {itens.map(item => {
        // O nome que a REGRA de aprendizado vai usar: o sujo do banco quando
        // existir (ver o JSDoc do checkbox), senão o limpo.
        const nomeDaRegra = item.nomeImportado ?? item.descricao;
        const aprender = !aprendizadoDesligado.has(item.id);

        return (
          <View key={item.id} data-testid={`pendente-${item.id}`}>
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                height: espaco.alturaLinha,
                gap: 12,
              }}
            >
              <Text
                style={{
                  ...valorDaLinha,
                  width: larguraData,
                  color: cor.tintaFraca,
                }}
              >
                {monthUtils.format(item.data, 'dd/MM')}
              </Text>

              <View style={{ flex: 1, minWidth: 0 }}>
                <Text style={texto.corpo}>
                  {item.descricao || t('(sem descrição)')}
                </Text>
                {item.conta && (
                  <Text style={{ ...texto.corpo, color: cor.tintaFraca }}>
                    {item.conta}
                  </Text>
                )}
              </View>

              <Text
                data-testid={`pendente-${item.id}-valor`}
                style={{
                  ...valorDaLinha,
                  width: larguraValor,
                  textAlign: 'right',
                  color: item.valor < 0 ? cor.tinta : cor.acento,
                }}
              >
                {integerToCurrency(item.valor)}
              </Text>

              <button
                type="button"
                data-testid={`pendente-${item.id}-classificar`}
                onClick={() => onClassificar(item)}
                style={{ ...acaoDeTexto, fontWeight: 600 }}
              >
                <Trans>Classificar</Trans>
              </button>
            </View>

            {/*
             * ⭐⭐ O APRENDIZADO, VISÍVEL (spec §5.3 e risco R5).
             *
             * Ao classificar, a tela manda
             * `send('transactions-batch-update', { updated, learnCategories:
             * true })`. O motor do Actual (`transaction-rules.ts`) olha as 5
             * últimas transações do mesmo estabelecimento e, com 3 iguais,
             * cria ou atualiza a regra sozinho — a partir daí aquele
             * estabelecimento chega já classificado.
             *
             * ⚠️ O import de banco NÃO passa esse flag (`sync.ts`, default
             * `false`). Se a gente também não passar, o app não aprende nada
             * e a fila vira trabalho eterno — 1.125 lançamentos hoje, outros
             * 1.125 no mês que vem.
             *
             * ⚠️⚠️ Por isso ele é LIGADO por padrão e MOSTRADO em texto, com
             * a caixa desmarcável: uma regra criada em silêncio é a forma
             * mais rápida de o app começar a errar sozinho sem o dono saber
             * por quê (risco R5). O aprendizado nunca é invisível.
             *
             * ⚠️ O nome exibido é o `imported_payee` (o sujo que o banco
             * manda, `PAG*MERCADOMUNDIAL`) quando ele existe — é ELE que se
             * repete mês a mês no Open Finance, e é por ele que o dono vai
             * reconhecer a regra.
             */}
            {nomeDaRegra && (
              <label
                data-testid={`pendente-${item.id}-aprender`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  paddingLeft: larguraData + 12,
                  paddingBottom: 8,
                  cursor: 'pointer',
                }}
              >
                <Checkbox
                  checked={aprender}
                  onChange={() => onAlternarAprendizado(item.id)}
                />
                <Text style={{ ...texto.corpo, color: cor.tintaFraca }}>
                  {t('sempre que aparecer {{nome}}, usar essa categoria', {
                    nome: nomeDaRegra,
                  })}
                </Text>
              </label>
            )}
          </View>
        );
      })}

      {restantes > 0 && (
        <button
          type="button"
          data-testid="sem-categoria-mostrar-mais"
          onClick={onMostrarMais}
          style={{ ...acaoDeTexto, textAlign: 'left' }}
        >
          {t('mostrar mais ({{restantes}} restantes)', { restantes })}
        </button>
      )}
    </View>
  );
}
